import { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef, ReactNode } from "react";
import { useARC } from "../ARC/ARCProvider";
import { getActionCallerBySocket } from "lib/utils/connection";
import type { ActionHandle, ActionCallbacks } from "arcjs/action/base";
import { ConnectionState } from "lib/consts/ARC/connection";
import {
    useGetBehaviourRuntimeQuery,
    type BehaviourActionFeedback,
} from "app/services/behaviourRulesApi";
import { useDockedActionGate } from "lib/hooks/useDockedActionGate";
import { useTranslation } from "react-i18next";

const GOAL_ID_STORAGE_KEY = "mission_execution_goal_id";
const ACTION_TIMEOUT_MS = 60000; // 60 seconds - longer timeout for missions
const BEHAVIOUR_ACTION_NAME = "/execute_behaviour";
const BEHAVIOUR_CANCEL_GOAL_SERVICE = `${BEHAVIOUR_ACTION_NAME}/_action/cancel_goal`;
const MISSION_PAUSE_SERVICE = "/mission_executor/pause";
const MISSION_RESUME_SERVICE = "/mission_executor/resume";
const MISSION_PAUSE_SERVICE_TYPE = "mission_executor_interfaces/srv/PauseMission";
const MISSION_RESUME_SERVICE_TYPE = "mission_executor_interfaces/srv/ResumeMission";

/** Cancel all goals on an action server (ROS 2 policy: zero UUID + zero stamp). */
const cancelAllActionGoalsRequest = () => ({
    goal_info: {
        goal_id: { uuid: new Array(16).fill(0) },
        stamp: { sec: 0, nanosec: 0 },
    },
});

/**
 * Step status from MissionExecutorState message
 */
interface StepStatus {
    action_id: string;
    action_type: string;
    action_name: string;
    status: string; // "pending", "running", "paused", "completed", "failed", "skipped"
    sub_feedback: string; // JSON serialized feedback (live, while running)
    sub_feedback_type: string;
    sub_result: string; // JSON serialized result
    sub_result_type: string;
    error: string;
}

/**
 * MissionExecutorState topic message
 */
interface MissionExecutorState {
    mission_id: string;
    mission_name: string;
    status: string; // "idle", "running", "paused", "completed", "failed", "cancelled", "returning"
    current_step_index: number;
    total_steps: number;
    step_statuses: StepStatus[];
    error: string;
}

/**
 * Mission execution feedback from action
 */
interface MissionExecuteFeedback {
    step_index: number;
    action_id: string;
    action_type: string;
    action_name: string;
    status: string;
    progress: number;
    sub_feedback: string;
    sub_feedback_type: string;
    sub_result: string;
    sub_result_type: string;
    external_input_required: boolean;
    error: string;
}

/**
 * Mission execution result from action
 */
interface MissionExecuteResult {
    success: boolean;
    message: string;
    final_state: string; // JSON serialized
    navigation_canceled?: boolean;
}

const isNavigationCanceledError = (errorStr: string): boolean => {
    const lower = errorStr.toLowerCase();
    if (lower.includes("navigation canceled")) {
        return true;
    }
    const jsonStart = errorStr.indexOf("{");
    if (jsonStart < 0) {
        return false;
    }
    try {
        const payload = JSON.parse(errorStr.slice(jsonStart)) as { message?: string };
        return String(payload.message ?? "").toLowerCase().includes("navigation canceled");
    } catch {
        return false;
    }
};

const buildNavigationCanceledResult = (errorStr: string): MissionExecuteResult => {
    const jsonStart = errorStr.indexOf("{");
    if (jsonStart >= 0) {
        try {
            const payload = JSON.parse(errorStr.slice(jsonStart)) as Record<string, unknown>;
            return {
                success: false,
                message: String(payload.message ?? "Navigation canceled"),
                final_state: JSON.stringify(payload),
                navigation_canceled: true,
            };
        } catch {
            // fall through
        }
    }
    return {
        success: false,
        message: "Navigation canceled",
        final_state: JSON.stringify({ canceled: true, navigation_canceled: true }),
        navigation_canceled: true,
    };
};

interface MissionExecutionContextType {
    // State from /mission_executor/state topic
    executorState: MissionExecutorState | null;

    // Local execution state
    isExecuting: boolean;
    currentMissionId: string | null;

    // Action feedback (from action callback, more frequent updates)
    lastFeedback: MissionExecuteFeedback | null;

    // Result
    executionResult: MissionExecuteResult | null;

    // Behaviour rule action feedback (e.g. return to launch after nav cancel)
    behaviourActionFeedback: BehaviourActionFeedback | null;

    // Execution control
    executeMission: (
        missionId: string,
        options?: { startStepIndex?: number; skipDockedGate?: boolean },
    ) => void;
    cancelMission: () => void;
    pauseMission: () => void;
    resumeMission: () => void;
    clearExecutionDisplay: () => void;

    // Step statuses keyed by action ID, reactive
    stepStatuses: Record<string, StepStatus>;
}

export const MissionExecutionContext = createContext<MissionExecutionContextType | undefined>(undefined);
MissionExecutionContext.displayName = "MissionExecution";

export const useMissionExecution = (): MissionExecutionContextType => {
    const context = useContext(MissionExecutionContext);
    if (!context) {
        throw new Error("useMissionExecution must be used within MissionExecutionProvider");
    }
    return context;
};

interface MissionExecutionProviderProps {
    children: ReactNode;
}

const TERMINAL_MISSION_STATUSES = new Set(["idle", "completed", "failed", "cancelled", "canceled", "succeeded"]);

const normalizeStatus = (status: unknown): string => String(status ?? "").trim().toLowerCase();

const resolveExecutorStatus = (msg: unknown): string => {
    if (!msg || typeof msg !== "object") return "";
    const raw = msg as Record<string, unknown>;
    return normalizeStatus(raw.status ?? raw.state);
};

const resolveExecutorMissionId = (msg: unknown): string => {
    if (!msg || typeof msg !== "object") return "";
    const raw = msg as Record<string, unknown>;
    return String(raw.mission_id ?? raw.missionId ?? "").trim();
};

const isRecoveryBehaviourActive = (
    executorState: MissionExecutorState | null,
    behaviourActionFeedback: BehaviourActionFeedback | null,
): boolean => {
    const status = resolveExecutorStatus(executorState);
    if (status === "returning") {
        return true;
    }
    return (
        behaviourActionFeedback?.type === "return_to_launch" &&
        behaviourActionFeedback?.status === "running"
    );
};

const mergeStepStatuses = (
    previous: Record<string, StepStatus>,
    incoming: StepStatus[] | undefined,
    replace: boolean
): Record<string, StepStatus> => {
    if (!Array.isArray(incoming) || incoming.length === 0) {
        return replace ? {} : previous;
    }
    const next = replace ? {} : { ...previous };
    for (const step of incoming) {
        if (!step?.action_id) continue;
        next[step.action_id] = {
            ...(next[step.action_id] ?? {}),
            ...step,
        };
    }
    return next;
};

const parseExecutorMessage = (message: unknown): MissionExecutorState | null => {
    if (!message) return null;
    if (typeof message === "string") {
        try {
            return JSON.parse(message) as MissionExecutorState;
        } catch {
            return null;
        }
    }
    if (typeof message === "object") {
        const raw = message as Record<string, unknown>;
        const wrappedData = raw.data;
        if (typeof wrappedData === "string") {
            try {
                return JSON.parse(wrappedData) as MissionExecutorState;
            } catch {
                return null;
            }
        }
        return raw as unknown as MissionExecutorState;
    }
    return null;
};

const MissionExecutionProvider = ({ children }: MissionExecutionProviderProps) => {
    const { t } = useTranslation("control");
    const { controlSocket, connectionState, callService } = useARC();
    // Action caller bound to the same socket as the control source (backend-agnostic).
    const actionCaller = useMemo(() => getActionCallerBySocket(controlSocket), [controlSocket]);
    const { runWithDockedGateAsync, dockedGateModal } = useDockedActionGate();

    // State from topic subscription
    const [executorState, setExecutorState] = useState<MissionExecutorState | null>(null);

    // Local execution tracking
    const [isExecuting, setIsExecuting] = useState(false);
    const [currentMissionId, setCurrentMissionId] = useState<string | null>(null);
    const [lastFeedback, setLastFeedback] = useState<MissionExecuteFeedback | null>(null);
    const [executionResult, setExecutionResult] = useState<MissionExecuteResult | null>(null);
    const [behaviourActionFeedback, setBehaviourActionFeedback] = useState<BehaviourActionFeedback | null>(null);
    const [behaviourPollingMissionId, setBehaviourPollingMissionId] = useState<string | null>(null);
    const [stepStatuses, setStepStatuses] = useState<Record<string, StepStatus>>({});

    // Action handle and goal tracking
    const [actionHandle, setActionHandle] = useState<ActionHandle | null>(null);
    const [rosbridgeGoalId, setRosbridgeGoalId] = useState<string | null>(null);

    // Timeout tracking
    const lastUpdateTime = useRef<number | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const currentMissionIdRef = useRef<string | null>(null);
    const lastStateMessageKeyRef = useRef<string | null>(null);
    const wasActiveRunRef = useRef(false);
    const goalSettledRef = useRef(false);
    const behaviourPollStartedAtRef = useRef<number | null>(null);
    const executorStateRef = useRef<MissionExecutorState | null>(null);
    const behaviourActionFeedbackRef = useRef<BehaviourActionFeedback | null>(null);
    const actionHandleRef = useRef<ActionHandle | null>(null);
    const executeMissionSendInFlightRef = useRef(false);

    const isExecutorRunActive = useCallback((msg: MissionExecutorState | null | undefined): boolean => {
        const status = resolveExecutorStatus(msg);
        const missionId = resolveExecutorMissionId(msg);
        return Boolean(missionId) && !TERMINAL_MISSION_STATUSES.has(status);
    }, []);

    const detachMissionActionHandle = useCallback(() => {
        const handle = actionHandleRef.current;
        actionHandleRef.current = null;
        setActionHandle(null);
        setRosbridgeGoalId(null);
        if (handle) {
            try {
                handle.stop();
            } catch (error) {
                console.warn("[MissionExecutionProvider] Failed to stop prior action handle:", error);
            }
        }
    }, []);

    useEffect(() => {
        currentMissionIdRef.current = currentMissionId;
    }, [currentMissionId]);

    useEffect(() => {
        actionHandleRef.current = actionHandle;
    }, [actionHandle]);

    useEffect(() => {
        executorStateRef.current = executorState;
    }, [executorState]);

    useEffect(() => {
        behaviourActionFeedbackRef.current = behaviourActionFeedback;
    }, [behaviourActionFeedback]);

    const startBehaviourPolling = useCallback((missionId: string) => {
        behaviourPollStartedAtRef.current = Date.now();
        setBehaviourActionFeedback(null);
        setBehaviourPollingMissionId(missionId);
    }, []);

    const { data: behaviourRuntime } = useGetBehaviourRuntimeQuery(undefined, {
        skip: !behaviourPollingMissionId,
        pollingInterval: behaviourPollingMissionId ? 1000 : 0,
    });

    const isFreshBehaviourAction = useCallback(
        (action: BehaviourActionFeedback | null | undefined, missionId: string) => {
            if (!action || action.type !== "return_to_launch") {
                return false;
            }
            if (action.mission_id && action.mission_id !== missionId) {
                return false;
            }
            const pollStartedAt = behaviourPollStartedAtRef.current ?? 0;
            if (action.updated_at && action.updated_at * 1000 < pollStartedAt - 1000) {
                return false;
            }
            return true;
        },
        [],
    );

    useEffect(() => {
        if (!behaviourPollingMissionId || !behaviourRuntime) {
            return;
        }

        const active = isFreshBehaviourAction(
            behaviourRuntime.active_action,
            behaviourPollingMissionId,
        )
            ? behaviourRuntime.active_action
            : null;
        const completed = isFreshBehaviourAction(
            behaviourRuntime.last_action,
            behaviourPollingMissionId,
        )
            ? behaviourRuntime.last_action
            : null;
        const action = active ?? completed;

        if (action) {
            setBehaviourActionFeedback(action);
            if (action.status !== "running") {
                setBehaviourPollingMissionId(null);
            }
            return;
        }

        const pollStartedAt = behaviourPollStartedAtRef.current ?? 0;
        if (Date.now() - pollStartedAt > 20000) {
            setBehaviourActionFeedback({
                type: "return_to_launch",
                status: "failed",
                message: "Return to launch was not triggered",
                mission_id: behaviourPollingMissionId,
            });
            setBehaviourPollingMissionId(null);
        }
    }, [behaviourPollingMissionId, behaviourRuntime, isFreshBehaviourAction]);

    useEffect(() => {
        if (!behaviourPollingMissionId) {
            return;
        }
        const timer = window.setTimeout(() => setBehaviourPollingMissionId(null), 10 * 60 * 1000);
        return () => window.clearTimeout(timer);
    }, [behaviourPollingMissionId]);

    // Reset state
    const resetState = useCallback(() => {
        console.log("[MissionExecutionProvider] Resetting state");
        setIsExecuting(false);
        setCurrentMissionId(null);
        setLastFeedback(null);
        setExecutionResult(null);
        setBehaviourActionFeedback(null);
        setBehaviourPollingMissionId(null);
        setStepStatuses({});
        detachMissionActionHandle();
        localStorage.removeItem(GOAL_ID_STORAGE_KEY);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        lastUpdateTime.current = null;
        wasActiveRunRef.current = false;
        lastStateMessageKeyRef.current = null;
        goalSettledRef.current = false;
    }, [detachMissionActionHandle]);

    // Subscribe to /mission_executor/state topic (single ROS-level subscription)
    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            console.log("[MissionExecutionProvider] Skipping /mission_executor/state subscription", {
                hasControlSocket: Boolean(controlSocket),
                connectionState,
            });
            return;
        }

        console.log("[MissionExecutionProvider] Subscribed to /mission_executor/state");

        const handleStateUpdate = (incoming: unknown) => {
            const msg = parseExecutorMessage(incoming);
            if (!msg) {
                console.warn("[MissionExecutionProvider] Failed to parse /mission_executor/state message", incoming);
                return;
            }
            const status = resolveExecutorStatus(msg);
            const missionId = resolveExecutorMissionId(msg);
            const isActiveRun = Boolean(missionId) && !TERMINAL_MISSION_STATUSES.has(status);
            const messageKey = JSON.stringify({
                status,
                missionId,
                current_step_index: msg.current_step_index,
                total_steps: msg.total_steps,
                step_statuses: msg.step_statuses,
            });
            if (messageKey === lastStateMessageKeyRef.current) {
                return;
            }
            lastStateMessageKeyRef.current = messageKey;

            const prevMissionId = currentMissionIdRef.current;
            const missionChanged = Boolean(missionId) && prevMissionId !== missionId;
            const isNewRun =
                isActiveRun &&
                (missionChanged || (!wasActiveRunRef.current && msg.current_step_index === 0));

            setExecutorState({
                ...msg,
                status,
                mission_id: missionId,
            });
            lastUpdateTime.current = Date.now();

            if (isActiveRun) {
                setIsExecuting(true);
                setCurrentMissionId(missionId);
                if (isNewRun) {
                    setExecutionResult(null);
                    setLastFeedback(null);
                }
                setStepStatuses((prev) =>
                    mergeStepStatuses(prev, msg.step_statuses, isNewRun)
                );
                wasActiveRunRef.current = true;
            } else if (TERMINAL_MISSION_STATUSES.has(status)) {
                setIsExecuting(false);
                setStepStatuses((prev) =>
                    mergeStepStatuses(prev, msg.step_statuses, false)
                );
                wasActiveRunRef.current = false;
                goalSettledRef.current = true;
                localStorage.removeItem(GOAL_ID_STORAGE_KEY);
                detachMissionActionHandle();
                // Do not clear/fail RTL feedback here. Mission status often becomes
                // cancelled/canceled after a nav-goal cancel that *triggers* RTL.
                // Explicit RTL cancel is handled only in cancelMission().
            }
        };

        // Backend-agnostic subscription via the control source (works for rosbridge and Foxglove).
        controlSocket.subscribe("/mission_executor/state", handleStateUpdate);

        return () => {
            lastStateMessageKeyRef.current = null;
            wasActiveRunRef.current = false;
            controlSocket.unsubscribe("/mission_executor/state", handleStateUpdate);
        };
    }, [controlSocket, connectionState, detachMissionActionHandle]);

    // Callbacks for a mission goal, shared by the execute and restore paths.
    const makeMissionGoalCallbacks = useCallback(
        (missionId: string): ActionCallbacks<MissionExecuteFeedback, MissionExecuteResult> => ({
            // Terminal success
            onResult: (result) => {
                if (goalSettledRef.current) return;
                goalSettledRef.current = true;
                const res = result;
                console.log("[MissionExecutionProvider] Mission completed:", res);
                if (!res.success && isNavigationCanceledError(res.message)) {
                    startBehaviourPolling(missionId);
                    setExecutionResult({ ...res, navigation_canceled: true });
                } else {
                    setExecutionResult(res);
                }
                setIsExecuting(false);
                localStorage.removeItem(GOAL_ID_STORAGE_KEY);
                detachMissionActionHandle();
                lastUpdateTime.current = Date.now();
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            },
            // Progress updates
            onFeedback: (feedback) => {
                const fb = feedback;
                console.log("[MissionExecutionProvider] Feedback:", fb);
                setLastFeedback(fb);
                setStepStatuses((prev) =>
                    mergeStepStatuses(
                        prev,
                        [
                            {
                                action_id: fb.action_id,
                                action_type: fb.action_type,
                                action_name: fb.action_name,
                                status: fb.status,
                                sub_feedback: fb.sub_feedback,
                                sub_feedback_type: fb.sub_feedback_type,
                                sub_result: fb.sub_result,
                                sub_result_type: fb.sub_result_type,
                                error: fb.error,
                            },
                        ],
                        false
                    )
                );
                lastUpdateTime.current = Date.now();
            },
            // Terminal failure / cancel / abort / expiry
            onFailed: (error) => {
                if (goalSettledRef.current) {
                    console.log("[MissionExecutionProvider] Ignoring post-result status:", error);
                    return;
                }
                goalSettledRef.current = true;
                console.log("[MissionExecutionProvider] Mission failed/canceled:", error);
                const errorStr = String(error);
                const lowerError = errorStr.toLowerCase();

                if (isNavigationCanceledError(errorStr)) {
                    startBehaviourPolling(missionId);
                    setExecutionResult(buildNavigationCanceledResult(errorStr));
                } else if (lowerError.includes("canceled") || lowerError.includes("status_canceled")) {
                    setExecutionResult({
                        success: false,
                        message: "Mission canceled",
                        final_state: JSON.stringify({ canceled: true }),
                    });
                } else {
                    setExecutionResult({
                        success: false,
                        message: errorStr,
                        final_state: JSON.stringify({ error: errorStr }),
                    });
                }

                setIsExecuting(false);
                localStorage.removeItem(GOAL_ID_STORAGE_KEY);
                detachMissionActionHandle();
                lastUpdateTime.current = Date.now();
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            },
        }),
        [detachMissionActionHandle, startBehaviourPolling]
    );

    // Execute mission (gated when docked — may stand / drive off the pile).
    // Skip the gate when the mission itself includes an upcoming Undock step.
    const executeMission = useCallback((
        missionId: string,
        options?: { startStepIndex?: number; skipDockedGate?: boolean },
    ) => {
        const run = async () => {
            if (!actionCaller) {
                console.error("[MissionExecutionProvider] Action caller not available for this control source");
                return;
            }
            if (executeMissionSendInFlightRef.current) {
                console.warn("[MissionExecutionProvider] Mission goal send already in progress");
                return;
            }
            if (isExecuting && isExecutorRunActive(executorStateRef.current)) {
                console.warn("[MissionExecutionProvider] Already executing a mission");
                return;
            }

            executeMissionSendInFlightRef.current = true;
            const startStepIndex = Math.max(0, Math.floor(options?.startStepIndex ?? 0));
            console.log(
                "[MissionExecutionProvider] Executing mission:",
                missionId,
                "start_step_index:",
                startStepIndex,
            );
            goalSettledRef.current = false;
            lastStateMessageKeyRef.current = null;
            detachMissionActionHandle();

            // Optimistic UI reset before the goal is accepted.
            setIsExecuting(true);
            setCurrentMissionId(missionId);
            // The state topic can still contain the terminal state from the previous
            // run until the executor publishes the first update for this run. Clear
            // it now so the mission panel cannot show the previous step/status/error
            // while the new goal is being accepted.
            setExecutorState(null);
            executorStateRef.current = null;
            setLastFeedback(null);
            setExecutionResult(null);
            setBehaviourActionFeedback(null);
            setBehaviourPollingMissionId(null);
            setStepStatuses({});
            lastUpdateTime.current = Date.now();

            try {
                const handle = await actionCaller.sendGoal<
                    { mission_id: string; start_step_index: number },
                    MissionExecuteFeedback,
                    MissionExecuteResult
                >(
                    "/execute_mission",
                    { mission_id: missionId, start_step_index: startStepIndex },
                    makeMissionGoalCallbacks(missionId),
                );

                console.log("[MissionExecutionProvider] Goal accepted, ID:", handle.goalId);
                actionHandleRef.current = handle;
                setActionHandle(handle);
                setRosbridgeGoalId(handle.goalId);
                localStorage.setItem(GOAL_ID_STORAGE_KEY, handle.goalId);
            } catch (error) {
                console.error("[MissionExecutionProvider] Failed to send goal:", error);
                setExecutionResult({
                    success: false,
                    message: String(error),
                    final_state: JSON.stringify({ error: String(error) }),
                });
                resetState();
            } finally {
                executeMissionSendInFlightRef.current = false;
            }
        };

        if (options?.skipDockedGate) {
            void run();
            return;
        }
        void runWithDockedGateAsync(run, t("docking.chargingBodyMissionRun"));
    }, [
        actionCaller,
        detachMissionActionHandle,
        isExecuting,
        isExecutorRunActive,
        makeMissionGoalCallbacks,
        resetState,
        runWithDockedGateAsync,
        t,
    ]);

    // Cancel mission or active recovery behaviour (RTL)
    const cancelMission = useCallback(() => {
        console.log("[MissionExecutionProvider] Cancel requested");

        if (connectionState !== ConnectionState.Connected || !controlSocket) {
            console.warn("[MissionExecutionProvider] Connection not available, clearing local state");
            resetState();
            return;
        }

        const recoveryActive = isRecoveryBehaviourActive(
            executorStateRef.current,
            behaviourActionFeedbackRef.current,
        );

        if (recoveryActive) {
            console.log("[MissionExecutionProvider] Cancelling recovery behaviour via /execute_behaviour");
            const missionId =
                resolveExecutorMissionId(executorStateRef.current) ||
                currentMissionIdRef.current ||
                behaviourActionFeedbackRef.current?.mission_id ||
                "";

            // Cancel-all (zero UUID) via the abstracted service caller.
            const request = cancelAllActionGoalsRequest();
            if (typeof callService === "function") {
                void callService(BEHAVIOUR_CANCEL_GOAL_SERVICE, request).catch((err) => {
                    console.warn("[MissionExecutionProvider] Recovery behaviour cancel failed:", err);
                });
            }

            setBehaviourActionFeedback({
                type: "return_to_launch",
                status: "failed",
                message: "Return to launch cancelled",
                mission_id: missionId || undefined,
            });
            setBehaviourPollingMissionId(null);
            return;
        }

        localStorage.removeItem(GOAL_ID_STORAGE_KEY);

        if (!actionHandle) {
            console.warn("[MissionExecutionProvider] No action handle to cancel; clearing local state only.");
            resetState();
            return;
        }

        console.log("[MissionExecutionProvider] Cancelling goal:", actionHandle.goalId);
        actionHandle.cancel().catch((error) => {
            console.error("[MissionExecutionProvider] Error cancelling:", error);
            resetState();
        });
        // The onFailed callback handles the terminal cancellation state.
    }, [actionHandle, controlSocket, connectionState, resetState, callService]);

    const pauseMission = useCallback(() => {
        console.log("[MissionExecutionProvider] Pause requested");
        if (connectionState !== ConnectionState.Connected || !controlSocket) {
            console.warn("[MissionExecutionProvider] Connection not available for pause");
            return;
        }
        if (typeof callService !== "function") {
            console.warn("[MissionExecutionProvider] callService unavailable for pause");
            return;
        }
        void callService(
            MISSION_PAUSE_SERVICE,
            {},
            { serviceType: MISSION_PAUSE_SERVICE_TYPE },
        ).catch((err) => {
            console.warn("[MissionExecutionProvider] Pause failed:", err);
        });
    }, [callService, connectionState, controlSocket]);

    const resumeMission = useCallback(() => {
        console.log("[MissionExecutionProvider] Resume requested");
        if (connectionState !== ConnectionState.Connected || !controlSocket) {
            console.warn("[MissionExecutionProvider] Connection not available for resume");
            return;
        }
        if (typeof callService !== "function") {
            console.warn("[MissionExecutionProvider] callService unavailable for resume");
            return;
        }
        void callService(
            MISSION_RESUME_SERVICE,
            {},
            { serviceType: MISSION_RESUME_SERVICE_TYPE },
        ).catch((err) => {
            console.warn("[MissionExecutionProvider] Resume failed:", err);
        });
    }, [callService, connectionState, controlSocket]);

    // Clear completed/failed run UI state when leaving the mission view (keep active runs intact)
    const clearExecutionDisplay = useCallback(() => {
        const status = resolveExecutorStatus(executorState);
        const missionId = resolveExecutorMissionId(executorState);
        const isActiveRun = Boolean(missionId) && !TERMINAL_MISSION_STATUSES.has(status);
        if (isExecuting || isActiveRun) {
            return;
        }
        setExecutionResult(null);
        setBehaviourActionFeedback(null);
        setBehaviourPollingMissionId(null);
        setStepStatuses({});
        setLastFeedback(null);
        setCurrentMissionId(null);
    }, [isExecuting, executorState]);

    // Handle disconnection
    useEffect(() => {
        if (connectionState === ConnectionState.Disconnected && isExecuting) {
            console.warn("[MissionExecutionProvider] Connection lost during execution");
            setExecutionResult({
                success: false,
                message: "Connection lost during execution",
                final_state: JSON.stringify({ error: "connection_lost" }),
            });
            resetState();
        }
    }, [connectionState, isExecuting, resetState]);

    // Timeout handling
    useEffect(() => {
        if (!isExecuting) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            return;
        }

        if (!lastUpdateTime.current) {
            lastUpdateTime.current = Date.now();
        }

        timeoutRef.current = setTimeout(() => {
            const elapsed = Date.now() - (lastUpdateTime.current || Date.now());
            if (elapsed >= ACTION_TIMEOUT_MS && isExecuting) {
                const status = resolveExecutorStatus(executorStateRef.current);
                // Hard pause holds the mission with no action feedback; do not treat as stale.
                if (status === "paused") {
                    return;
                }
                console.warn(`[MissionExecutionProvider] Execution timeout: ${elapsed}ms`);
                setExecutionResult({
                    success: false,
                    message: `Execution timeout - no updates for ${Math.round(elapsed / 1000)}s`,
                    final_state: JSON.stringify({ error: "timeout" }),
                });
                resetState();
            }
        }, ACTION_TIMEOUT_MS);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [isExecuting, resetState]);

    // Restore from localStorage on mount — rejoin the goal by id if the backend supports it.
    // Live status always comes from /mission_executor/state regardless; monitorGoal adds a
    // handle for cancellation + result/feedback (Foxglove). roslib can't rejoin — degrades to
    // state-topic-only, same as before.
    useEffect(() => {
        const storedGoalId = localStorage.getItem(GOAL_ID_STORAGE_KEY);
        if (!storedGoalId || rosbridgeGoalId || !actionCaller || connectionState !== ConnectionState.Connected) {
            return;
        }
        // Strip any legacy "send_action_goal:/execute_mission:<uuid>" prefix.
        const goalId = storedGoalId.includes(":") ? storedGoalId.split(":").pop()! : storedGoalId;
        console.log("[MissionExecutionProvider] Restoring from localStorage:", goalId);
        setRosbridgeGoalId(goalId);
        setIsExecuting(true);
        lastUpdateTime.current = Date.now();
        goalSettledRef.current = false;

        const missionId =
            resolveExecutorMissionId(executorStateRef.current) || currentMissionIdRef.current || "";
        actionCaller
            .monitorGoal<MissionExecuteFeedback, MissionExecuteResult>(
                "/execute_mission",
                goalId,
                makeMissionGoalCallbacks(missionId)
            )
            .then((handle) => {
                actionHandleRef.current = handle;
                setActionHandle(handle);
            })
            .catch((err) => {
                console.warn(
                    "[MissionExecutionProvider] Could not rejoin goal (live status still comes from /mission_executor/state):",
                    err
                );
            });
    }, [actionCaller, rosbridgeGoalId, connectionState, makeMissionGoalCallbacks]);

    const value: MissionExecutionContextType = {
        executorState,
        isExecuting,
        currentMissionId,
        lastFeedback,
        executionResult,
        behaviourActionFeedback,
        executeMission,
        cancelMission,
        pauseMission,
        resumeMission,
        clearExecutionDisplay,
        stepStatuses,
    };

    return (
        <MissionExecutionContext.Provider value={value}>
            {dockedGateModal}
            {children}
        </MissionExecutionContext.Provider>
    );
};

export default MissionExecutionProvider;
